// ============================================================
// ✅ EmployeModal.jsx - Création d'un employé
// Règles claires :
// - SuperAdmin (sans société) = EstSuperAdmin=true, ID_Societe=null
// - Admin/Manager/User = EstSuperAdmin=false, ID_Societe=obligatoire
// - Observateur = Role user, ID_Manager=NULL, ID_Postes=NULL, champs pro optionnels
// ============================================================

import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { X, User, Briefcase, Mail, AlertCircle, Eye } from 'lucide-react';
import SuccessModal from '../../components/ui/SuccessModal';

export default function EmployeModal({ isOpen, onClose, onSuccess }) {
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('civil');
  const [postes, setPostes] = useState([]);
  const [managers, setManagers] = useState([]);
  const [societes, setSocietes] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isObservateur, setIsObservateur] = useState(false);  // ✅ Nouveau
  
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  
  const [formData, setFormData] = useState({
    Matricule: '', Nom: '', Prenom: '', CIN: '',
    DateNaissance: '', DateEmbauche: '', Email: '', Telephone: '',
    StatutContrat: 'CDI', ID_Postes: '', ID_Societe: '',
    Nationalite: 'Marocaine', Sexe: 'M',
    SituationFamiliale: 'Célibataire', NombreEnfants: 0,
    ID_Manager: '', EstActif: true
  });

  const { user } = useUser();
  const isSuperAdmin = user?.EstSuperAdmin === 1 || user?.EstSuperAdmin === true;

  const tabs = [
    { id: 'civil', label: '👤 État civil', icon: <User size={16} /> },
    { id: 'pro', label: '💼 Professionnel', icon: <Briefcase size={16} /> },
    { id: 'contact', label: '📞 Contact', icon: <Mail size={16} /> }
  ];

  useEffect(() => {
    if (isOpen) {
      resetForm();
      if (isSuperAdmin) fetchSocietes();
    }
  }, [isOpen]);

  const fetchSocietes = async () => {
    try { const res = await api.get('/societes'); setSocietes(res.data.societes || []); }
    catch (err) { /* Silencieux */ }
  };

  const fetchPostes = async (idSociete = null) => {
    try {
      const societe = idSociete || formData.ID_Societe || user?.ID_Societe;
      const params = isSuperAdmin && !societe ? {} : { ID_Societe: societe || 1 };
      const res = await api.get('/postes', { params });
      setPostes(res.data.postes || []);
    } catch (err) { /* Silencieux */ }
  };

  const fetchManagers = async (idSociete = null) => {
    try {
      const societe = idSociete || formData.ID_Societe || user?.ID_Societe;
      const res = await api.get('/employes/managers', {
        params: { ID_Societe: isSuperAdmin ? (societe || null) : user?.ID_Societe }
      });
      setManagers(res.data.managers || []);
    } catch (err) { /* Silencieux */ }
  };

  const handleSocieteChange = (idSociete) => {
    setFormData({ ...formData, ID_Societe: idSociete, ID_Postes: '', ID_Manager: '' });
    if (idSociete) { fetchPostes(idSociete); fetchManagers(idSociete); }
  };

    const resetForm = () => {
    setFormData({
      Matricule: '', Nom: '', Prenom: '', CIN: '',
      DateNaissance: '', DateEmbauche: '', Email: '', Telephone: '',
      StatutContrat: 'CDI', ID_Postes: '', ID_Societe: user?.ID_Societe || '',
      Nationalite: 'Marocaine', Sexe: 'M',
      SituationFamiliale: 'Célibataire', NombreEnfants: 0,
      ID_Manager: '', EstActif: true
    });
    setIsAdmin(false);
    setIsObservateur(false);  // ✅ Ajouter cette ligne
    setActiveTab('civil');
    setError('');
    if (user?.ID_Societe) { fetchPostes(user.ID_Societe); fetchManagers(user.ID_Societe); }
  };

  // ==========================================================
// 💾 SOUMISSION
// ==========================================================
const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.Email.trim()) {
      setError("L'email est obligatoire");
      return;
    }

    setLoading(true);

    // ✅ Déterminer le rôle
    let Role = 'user';
    if (isObservateur) Role = 'observateur';
    else if (isAdmin) Role = 'admin';
    
    const EstSuperAdmin = isAdmin && !formData.ID_Societe;

    // ✅ Observateur : ID_Manager = NULL, ID_Postes = NULL
    let idManager = formData.ID_Manager;
    if (isObservateur) {
      idManager = null;
    } else if (idManager === 'self' || idManager === '' || idManager === 0 || idManager === '0') {
      idManager = null;
    }
    if (idManager) idManager = parseInt(idManager);

    try {
      const payload = {
        Matricule: formData.Matricule || '',
        Nom: formData.Nom || '',
        Prenom: formData.Prenom || '',
        CIN: formData.CIN || null,
        DateNaissance: formData.DateNaissance || null,
        DateEmbauche: isObservateur ? null : (formData.DateEmbauche || null),
        Email: formData.Email || '',
        Telephone: formData.Telephone || null,
        StatutContrat: isObservateur ? null : (formData.StatutContrat || 'CDI'),
        ID_Postes: isObservateur ? null : (formData.ID_Postes || null),
        Nationalite: formData.Nationalite || null,
        Sexe: formData.Sexe || 'M',
        SituationFamiliale: formData.SituationFamiliale || null,
        NombreEnfants: parseInt(formData.NombreEnfants) || 0,
        ID_Manager: idManager,
        EstActif: formData.EstActif !== undefined ? formData.EstActif : true,
        Role,
        EstSuperAdmin,
        ID_Societe: EstSuperAdmin ? null : (formData.ID_Societe || user?.ID_Societe || 1),
      };

      const response = await api.post('/employes', payload);
      
      if (response.data.success) {
        setSuccessMessage('Employé créé avec succès.');
        setEmailSent(true);
        setShowSuccessModal(true);
      } else {
        setError(response.data.message);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => { setShowSuccessModal(false); onSuccess(); onClose(); };
  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-emerald-600">➕ Nouvel employé</h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
          </div>

          <div className="flex gap-1 border-b mb-4">
            {tabs.map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm rounded-t-lg flex items-center gap-2 transition ${activeTab === tab.id ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            
            {/* ============================================ */}
            {/* ONGLET : ÉTAT CIVIL                           */}
            {/* ============================================ */}
            {activeTab === 'civil' && (
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Matricule *</label><input type="text" className="w-full p-2 border rounded-lg" value={formData.Matricule} onChange={(e) => setFormData({...formData, Matricule: e.target.value})} required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">CIN</label><input type="text" className="w-full p-2 border rounded-lg" value={formData.CIN} onChange={(e) => setFormData({...formData, CIN: e.target.value})} /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label><input type="text" className="w-full p-2 border rounded-lg" value={formData.Nom} onChange={(e) => setFormData({...formData, Nom: e.target.value})} required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label><input type="text" className="w-full p-2 border rounded-lg" value={formData.Prenom} onChange={(e) => setFormData({...formData, Prenom: e.target.value})} required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Date de naissance</label><input type="date" className="w-full p-2 border rounded-lg" value={formData.DateNaissance} onChange={(e) => setFormData({...formData, DateNaissance: e.target.value})} /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Nationalité</label><input type="text" className="w-full p-2 border rounded-lg" value={formData.Nationalite} onChange={(e) => setFormData({...formData, Nationalite: e.target.value})} /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Sexe</label><select className="w-full p-2 border rounded-lg" value={formData.Sexe} onChange={(e) => setFormData({...formData, Sexe: e.target.value})}><option value="M">Masculin</option><option value="F">Féminin</option></select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Situation familiale</label><select className="w-full p-2 border rounded-lg" value={formData.SituationFamiliale} onChange={(e) => setFormData({...formData, SituationFamiliale: e.target.value})}><option value="Célibataire">Célibataire</option><option value="Marié">Marié(e)</option><option value="Divorcé">Divorcé(e)</option><option value="Veuf">Veuf/Veuve</option></select></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Nombre d'enfants</label><input type="number" className="w-full p-2 border rounded-lg" value={formData.NombreEnfants} onChange={(e) => setFormData({...formData, NombreEnfants: parseInt(e.target.value) || 0})} /></div>
              </div>
            )}

            {/* ============================================ */}
            {/* ONGLET : PROFESSIONNEL                        */}
            {/* ============================================ */}
            {activeTab === 'pro' && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date d'embauche {!isObservateur && '*'}
                  </label>
                  <input type="date" className="w-full p-2 border rounded-lg" 
                    value={formData.DateEmbauche} 
                    onChange={(e) => setFormData({...formData, DateEmbauche: e.target.value})} 
                    required={!isObservateur} 
                    disabled={isObservateur} />
                  {isObservateur && <p className="text-xs text-gray-400 mt-1">Non requis pour observateur</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Statut contrat</label>
                  <select className="w-full p-2 border rounded-lg" 
                    value={formData.StatutContrat} 
                    onChange={(e) => setFormData({...formData, StatutContrat: e.target.value})}
                    disabled={isObservateur}>
                    <option value="CDI">CDI</option><option value="CDD">CDD</option><option value="Stage">Stage</option><option value="Freelance">Freelance</option>
                  </select>
                </div>
                
                {/* Société */}
                {isSuperAdmin && (
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Société {!isAdmin ? '*' : '(optionnel pour SuperAdmin)'}
                    </label>
                    <select className="w-full p-2 border rounded-lg" value={formData.ID_Societe || ''} onChange={(e) => handleSocieteChange(e.target.value)} required={!isAdmin}>
                      <option value="">Sélectionner une société</option>
                      {societes.map(s => <option key={s.ID_Societe} value={s.ID_Societe}>{s.RaisonSociale} ({s.Sigle})</option>)}
                    </select>
                    {isAdmin && <p className="text-xs text-gray-400 mt-1">Sans société = SuperAdmin global.</p>}
                  </div>
                )}

                {/* Poste */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Poste</label>
                  <select className="w-full p-2 border rounded-lg" 
                    value={formData.ID_Postes} 
                    onChange={(e) => setFormData({...formData, ID_Postes: e.target.value})}
                    disabled={isObservateur}>
                    <option value="">Sélectionner un poste</option>
                    {postes.map(p => <option key={p.ID_Postes} value={p.ID_Postes}>{p.Nom_Postes}</option>)}
                  </select>
                </div>

                {/* Manager */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Manager (N+1)</label>
                  <select className="w-full p-2 border rounded-lg" 
                    value={formData.ID_Manager} 
                    onChange={(e) => setFormData({...formData, ID_Manager: e.target.value})}
                    disabled={isObservateur}>
                    <option value="">Aucun</option>
                    <option value="self">Soi-même (top niveau)</option>
                    <option disabled>──── Managers disponibles ────</option>
                    {managers.map(m => <option key={m.ID_Employes} value={m.ID_Employes}>{m.Prenom} {m.Nom} {m.Role === 'admin' ? '👑' : '(Manager)'}</option>)}
                  </select>
                  {isObservateur && <p className="text-xs text-gray-400 mt-1">Observateur = sans manager</p>}
                </div>
                
                {/* Info société pour non-SuperAdmin */}
                {!isSuperAdmin && (
                  <div className="col-span-2 p-2.5 bg-gray-50 rounded-lg text-sm text-gray-600">
                    Société : <strong>{user?.SocieteNom || `ID: ${user?.ID_Societe}`}</strong>
                  </div>
                )}
                
                {/* ✅ Checkbox Observateur */}
                <div className="col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={isObservateur} 
                      onChange={(e) => {
                        setIsObservateur(e.target.checked);
                        if (e.target.checked) setIsAdmin(false); // Incompatible avec admin
                      }} 
                      className="w-4 h-4 text-amber-500 rounded" />
                    <Eye size={16} className="text-amber-500" />
                    <span className="text-sm text-gray-700">Observateur (consultation seule)</span>
                  </label>
                  <p className="text-xs text-gray-400 mt-1 ml-6">Accès au planning uniquement, sans création de demande. Champs professionnels non requis.</p>
                </div>

                {/* Admin checkbox */}
                {!isObservateur && (
                  <div className="col-span-2">
                    <label className="flex items-center gap-2">
                      <input type="checkbox" checked={isAdmin} 
                        onChange={(e) => {
                          setIsAdmin(e.target.checked);
                          if (e.target.checked) setIsObservateur(false); // Incompatible avec observateur
                        }} 
                        className="w-4 h-4 text-purple-500 rounded" />
                      <span className="text-sm text-gray-700">Administrateur</span>
                    </label>
                  </div>
                )}
                
                <div className="col-span-2">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={formData.EstActif} onChange={(e) => setFormData({...formData, EstActif: e.target.checked})} /><span className="text-sm text-gray-700">Employé actif</span></label>
                </div>
              </div>
            )}

            {/* ============================================ */}
            {/* ONGLET : CONTACT                              */}
            {/* ============================================ */}
            {activeTab === 'contact' && (
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Email *</label><input type="email" className="w-full p-2 border rounded-lg" value={formData.Email} onChange={(e) => setFormData({...formData, Email: e.target.value})} required /></div>
                <div><label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label><input type="tel" className="w-full p-2 border rounded-lg" value={formData.Telephone} onChange={(e) => setFormData({...formData, Telephone: e.target.value})} /></div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition">Annuler</button>
              <button type="submit" disabled={loading} className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition disabled:opacity-50">{loading ? 'Création...' : "Créer l'employé"}</button>
            </div>
          </form>
        </div>
      </div>
      <SuccessModal isOpen={showSuccessModal} onClose={handleSuccessClose} title="✅ Employé créé" message={successMessage} emailSent={emailSent} />
    </>
  );
}